// Minimal fetch-based API client — replaces axios to shave ~30KB gzip off the
// LCP-critical main bundle. Preserves the same call shape (`api.get(url)`,
// `api.post(url, body)` returning `{ data }`) so call sites do not change.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const DEFAULT_TIMEOUT_MS = 25000;

function buildUrl(path, params) {
  let url = /^https?:\/\//i.test(path) ? path : `${API}${path.startsWith("/") ? path : `/${path}`}`;
  if (params && typeof params === "object") {
    const qs = Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== null && v !== "")
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join("&");
    if (qs) url += (url.includes("?") ? "&" : "?") + qs;
  }
  return url;
}

async function request(method, path, body, options = {}) {
  const controller = new AbortController();
  const timeoutMs = options.timeout || DEFAULT_TIMEOUT_MS;
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  // FormData (file uploads) is sent as-is: the browser sets the multipart boundary.
  const isForm = typeof FormData !== "undefined" && body instanceof FormData;

  try {
    const res = await fetch(buildUrl(path, options.params), {
      method,
      headers: {
        ...(isForm ? {} : { "Content-Type": "application/json" }),
        ...(options.headers || {}),
      },
      body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
      signal: controller.signal,
    });

    let data = null;
    const text = await res.text();
    if (text) {
      try {
        data = JSON.parse(text);
      } catch (err) {
        // JSON parse failed: keep raw text.
        void err;
        data = text;
      }
    }

    if (!res.ok) {
      const err = new Error(`HTTP ${res.status}`);
      err.status = res.status;
      err.response = { status: res.status, data };
      throw err;
    }

    return { data, status: res.status };
  } finally {
    clearTimeout(timer);
  }
}

export const api = {
  get: (path, options) => request("GET", path, undefined, options),
  post: (path, body, options) => request("POST", path, body, options),
  put: (path, body, options) => request("PUT", path, body, options),
  patch: (path, body, options) => request("PATCH", path, body, options),
  delete: (path, options) => request("DELETE", path, undefined, options),
};

export const COMPANY = {
  name: "All Service di La Rocca Cristian",
  short_name: "All Service Toscana",
  tagline_it: "Ricerca perdite acqua — senza demolizioni inutili",
  tagline_en: "Water leak detection — without needless demolition",
  phone_display: "+39 351 062 4386",
  phone_tel: "+393510624386",
  whatsapp_number: "393510624386", // international format, no + for wa.me
  email: "allservicetoscana@gmail.com",
  hours: "H24 · 7 giorni su 7",
  hours_en: "24/7 · every day",
  vat: "P.IVA IT02132210473",
  address_full: "Via Guglielmo Marconi 33, 51010 Massa e Cozzile (PT)",
  address_short: "Toscana · Liguria · Umbria · Alto Lazio",
  owner: "Cristian La Rocca",
  // Official external profiles (provided by the owner). Keep in sync with the
  // JSON-LD `sameAs` in public/index.html.
  social: {
    facebook: "https://www.facebook.com/profile.php?id=61594888928665",
    instagram: "https://www.instagram.com/bagnoman.allservice/",
    // Official Google Business profile link ("Bagnoman - All Service di La Rocca Cristian"),
    // copied by the owner from the profile's Share UI. Verified 2026-10-03: 302 -> Google
    // business panel (kgmid /g/11njxfgsv8). Use THIS for every link to the company profile.
    google_business: "https://share.google/sWL8N7BOthIkdDFDK",
    // Legacy Maps search for the same business (kept only inside JSON-LD sameAs for continuity).
    google_maps_search: "https://www.google.com/maps/search/?api=1&query=All+Service+di+La+Rocca+Cristian+Massa+e+Cozzile",
  },
};

export const SOCIAL_LINKS = [
  { key: "facebook", label: "Facebook", href: COMPANY.social.facebook },
  { key: "instagram", label: "Instagram", href: COMPANY.social.instagram },
  { key: "google", label_it: "Profilo Google Business", label_en: "Google Business profile", href: COMPANY.social.google_business },
];

export function waLink(prefilledMessage) {
  const msg = encodeURIComponent(prefilledMessage || "Salve, ho bisogno di assistenza per una perdita d'acqua.");
  return `https://wa.me/${COMPANY.whatsapp_number}?text=${msg}`;
}
export function telLink() {
  return `tel:${COMPANY.phone_tel}`;
}

// --- Google Ads conversion tracking wiring ---------------------------------
// Lazy-imports gtag helpers to keep this module side-effect-free for
// non-conversion call sites. Resolves to no-op when the gtag IDs are not yet
// configured in /app/frontend/src/lib/gtag.js.
async function fireConversionSafe(name, payload) {
  try {
    const mod = await import("@/lib/gtag");
    if (typeof mod[name] === "function") mod[name](payload);
  } catch (err) {
    void err; // tracking errors must never break the UX
  }
}

export const trackCallEvent = () => fireConversionSafe("trackCall");
export const trackWhatsAppEvent = () => fireConversionSafe("trackWhatsApp");
/**
 * Track form submit con Enhanced Conversions (first-party data).
 * Passa `userData = { email, phone, name }` per popolare il tag Google Ads
 * con i dati first-party necessari alle Conversioni Avanzate.
 */
export const trackFormSubmitEvent = (userData) =>
  fireConversionSafe("trackFormSubmit", userData);
export const trackCalculatorEvent = (userData) =>
  fireConversionSafe("trackCalculatorLead", userData);
